import type { StreamEvent } from "./types";

/**
 * Parses an SSE byte stream (as produced by the Ptero-Cloud daemon's sseFrame())
 * into a typed StreamEvent async iterable.
 *
 * Chunk-safe: handles frames split across multiple stream chunks.
 * Frame-boundary-safe: splits on \n\n, buffers trailing partial frame.
 * Forward-compat: unknown event names are dropped.
 */
export async function* parseSseStream(
  stream: ReadableStream<Uint8Array>,
): AsyncGenerator<StreamEvent, void, void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8", { fatal: false });
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const ev = parseFrame(frame);
        if (ev) yield ev;
      }
    }
    // Flush any remaining decoder state + final partial frame.
    buffer += decoder.decode();
    const tail = buffer.trim();
    if (tail.length > 0) {
      const ev = parseFrame(tail);
      if (ev) yield ev;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

function parseFrame(frame: string): StreamEvent | null {
  let eventName = "message";
  const dataLines: string[] = [];

  for (const rawLine of frame.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.length === 0 || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    if (colon === -1) continue;
    const field = line.slice(0, colon);
    let value = line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);
    if (field === "event") eventName = value;
    else if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  const dataStr = dataLines.join("\n");

  // Only handle daemon-emitted events; drop everything else for forward-compat.
  if (
    eventName !== "token" &&
    eventName !== "done" &&
    eventName !== "error" &&
    eventName !== "progress"
  ) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(dataStr);
  } catch {
    return { type: "error", error: "malformed sse frame" };
  }

  if (eventName === "token") {
    if (typeof parsed !== "string") return { type: "error", error: "malformed sse frame" };
    return { type: "token", text: parsed };
  }
  if (eventName === "done") {
    const subtype = typeof parsed === "object" && parsed !== null && typeof (parsed as { subtype?: unknown }).subtype === "string"
      ? (parsed as { subtype: string }).subtype
      : undefined;
    return subtype !== undefined ? { type: "done", subtype } : { type: "done" };
  }
  if (eventName === "progress") {
    if (typeof parsed !== "object" || parsed === null) return null;
    const p = parsed as { kind?: unknown; label?: unknown; meta?: unknown };
    const validKinds = new Set(["tool_use", "tool_ok", "tool_error", "thinking"]);
    if (typeof p.kind !== "string" || !validKinds.has(p.kind)) return null;
    if (typeof p.label !== "string") return null;
    return {
      type: "progress",
      kind: p.kind as "tool_use" | "tool_ok" | "tool_error" | "thinking",
      label: p.label,
      meta: (typeof p.meta === "object" && p.meta !== null ? p.meta : {}) as Record<string, unknown>,
    };
  }
  // error
  const err = typeof parsed === "object" && parsed !== null && typeof (parsed as { error?: unknown }).error === "string"
    ? (parsed as { error: string }).error
    : "unknown";
  return { type: "error", error: err };
}
